import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md font-body font-semibold ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90 text-[16px]",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90 text-[16px]",
        outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground text-[16px]",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80 text-[16px]",
        ghost: "hover:bg-accent hover:text-accent-foreground text-[16px]",
        link: "text-primary underline-offset-4 hover:underline text-[16px]",
        cta: "bg-primary text-primary-foreground hover:bg-[#FAA55E] rounded-[5px] font-body font-semibold text-[16px] shadow-lg hover:shadow-[0_15px_10px_0_rgba(250,165,94,0.32)] transition-all duration-300",
        "cta-outline": "border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground rounded-[5px] font-body font-semibold text-[16px] transition-all duration-300",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-12 rounded-md px-10",
        xl: "h-14 rounded-md px-12",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
